import { ChevronLeft, ErrorOutline } from "@mui/icons-material";
import { Button, Typography } from "@mui/material";
import { tracker } from "@openreplay/tracker";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  BackButton,
  FooterContent,
  SidePadding,
  StickyFooter,
  StyledContainer,
  StyledForm,
  StyledHeader,
  SubmitArea,
  SubmitButton,
  SubmitError,
} from "@components/FormStepper/FormLayout";
import FormStepper from "@components/FormStepper/FormStepper";
import Loading from "@components/Loading";

import {
  ACCESS_REQUEST_DETAIL_SEGMENT,
  ACCESS_REQUESTS_ROUTE,
} from "@helpers/constants";
import { ACCESS_REQUEST_EVENTS } from "@helpers/events";

import { useCreateAccessRequestMutation } from "../../../store/api/accessRequestsApi";
import { useGetAllAccountsQuery } from "../../../store/api/accountsApi";
import { useGetResourceMetadataQuery } from "../../../store/api/resourceApi";
import { toResourceType } from "../../../store/api/resourceTypes";
import ActionSelectionField from "./components/ActionSelectionField";
import AdditionalOptionsField from "./components/AdditionalOptionsField";
import RecipientSelectionField from "./components/RecipientSelectionField";
import RequestReasonField from "./components/RequestReasonField";
import ResourcePicker from "./components/ResourcePicker";
import {
  getDefaultScope,
  getExpirationDate,
  INITIAL_FORM_STATE,
  isFormValid,
} from "./data/types";
import type {
  AccessRequestFormState,
  SelectedResource,
  SelectedTarget,
} from "./data/types";

const STEP_NAMES = [
  "select_target",
  "select_resource",
  "select_action",
  "additional_options",
  "request_reason",
];

function NewAccessRequestForm() {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const prefilledResourceParam = searchParams.get("resource");
  const prefilledTypeParam = toResourceType(searchParams.get("type"));
  const hasPrefill = !!prefilledResourceParam;

  // Resolve the resource metadata even when the user lacks read access; the
  // metadata endpoint is the single source of truth for name/type/urn.
  const {
    data: prefillMetadata,
    isFetching: prefillFetching,
    isError: prefillErrored,
    refetch: refetchPrefillMetadata,
  } = useGetResourceMetadataQuery(
    // ?resource= may be a name rather than an id (e.g. from a data detail URL),
    // so pass ?type= when present to resolve names shared across types.
    // Without it (e.g. a hand-typed URL) the lookup is untyped and may conflict.
    { identifier: prefilledResourceParam ?? "", type: prefilledTypeParam },
    { skip: !hasPrefill },
  );

  // A prefill error is almost always our own rate limiter superseding this
  // call (see rateLimitMetadata in resourceApi.ts), not a real failure.
  // Retry once rather than silently leaving the form unprefilled.
  const hasRetriedPrefill = useRef(false);
  useEffect(() => {
    if (prefillErrored && !hasRetriedPrefill.current) {
      hasRetriedPrefill.current = true;
      refetchPrefillMetadata();
    }
  }, [prefillErrored, refetchPrefillMetadata]);

  const prefilledResource = useMemo<SelectedResource | null>(() => {
    if (!prefillMetadata) return null;
    const type = toResourceType(prefillMetadata.type);
    if (!type) return null;
    return {
      id: prefillMetadata.id,
      name:
        prefillMetadata.fullName || prefillMetadata.name || prefillMetadata.id,
      type,
      urn: prefillMetadata.urn,
      organization: prefillMetadata.root,
    };
  }, [prefillMetadata]);

  const [step, setStep] = useState(0);

  const handleStepSelect = useCallback((index: number) => {
    tracker.event(
      ACCESS_REQUEST_EVENTS.FORM_STEP_VIEWED,
      STEP_NAMES[index] ?? String(index),
    );
    setStep(index);
  }, []);
  const [formState, setFormState] =
    useState<AccessRequestFormState>(INITIAL_FORM_STATE);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showValidation, setShowValidation] = useState(false);

  const { data: accounts } = useGetAllAccountsQuery();
  const [createAccessRequest, { isLoading: isSubmitting }] =
    useCreateAccessRequestMutation();

  const resolveAccount = useCallback(
    (accountRef: string) => {
      const account = accounts?.find(
        (a) => a.id === accountRef || a.fullName === accountRef,
      );
      return {
        id: account?.id ?? accountRef,
        name: account?.fullName ?? accountRef,
      };
    },
    [accounts],
  );

  const updateFormState = useCallback(
    (updates: Partial<AccessRequestFormState>) => {
      setFormState((prev) => ({ ...prev, ...updates }));
    },
    [],
  );

  const hasAppliedPrefill = useRef(false);
  useEffect(() => {
    if (prefilledResource && !hasAppliedPrefill.current) {
      hasAppliedPrefill.current = true;
      updateFormState({
        resource: prefilledResource,
        resourceType: prefilledResource.type,
      });
      setStep(4);
    }
  }, [prefilledResource, updateFormState]);

  // Set the default scope once both the prefilled resource and accounts resolve.
  const hasInitializedScope = useRef(false);
  useEffect(() => {
    if (prefilledResource && !hasInitializedScope.current && accounts) {
      const defaultScope = getDefaultScope(prefilledResource, resolveAccount);
      updateFormState({ scope: defaultScope });
      hasInitializedScope.current = true;
    }
    // only re-run when async data (accounts / prefill) becomes available
    // eslint-disable-next-line
  }, [accounts, prefilledResource]);

  const handleActionChange = useCallback(
    (action: string | null) => updateFormState({ action }),
    [updateFormState],
  );

  const handleTargetChange = useCallback(
    (target: SelectedTarget | null) => updateFormState({ target }),
    [updateFormState],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    tracker.event(ACCESS_REQUEST_EVENTS.FORM_SUBMITTED);
    setShowValidation(true);
    setSubmitError(null);

    if (!isFormValid(formState)) {
      return;
    }

    if (!formState.resource || !formState.action || !formState.target) {
      return;
    }

    try {
      const response = await createAccessRequest({
        targetType: formState.targetType,
        targetId: formState.target.id,
        body: {
          action: formState.action,
          resource: formState.resource.id,
          scope: formState.scope?.id ?? "",
          shareable: formState.shareable,
          expiration: getExpirationDate(formState.expirationDays),
          requestReason: formState.requestReason,
        },
      }).unwrap();

      const requestId = response.requestId;
      tracker.event(
        ACCESS_REQUEST_EVENTS.FORM_SUBMIT_SUCCEEDED,
        requestId ?? "",
      );
      if (requestId) {
        navigate(
          `${ACCESS_REQUESTS_ROUTE}/${ACCESS_REQUEST_DETAIL_SEGMENT}/${requestId}`,
          {
            replace: true,
            state: { canGoBack: true },
          },
        );
      } else {
        navigate(ACCESS_REQUESTS_ROUTE);
      }
    } catch (error) {
      tracker.event(ACCESS_REQUEST_EVENTS.FORM_SUBMIT_FAILED);
      console.error("Failed to create access request:", error);
      setSubmitError(
        (error as { data?: { error?: { message?: string } } })?.data?.error
          ?.message ?? "Failed to create access request. Please try again.",
      );
    }
  };

  const handleCancel = () => {
    tracker.event(ACCESS_REQUEST_EVENTS.FORM_CANCELLED);
    navigate(ACCESS_REQUESTS_ROUTE);
  };

  const handleBack = () => {
    tracker.event(ACCESS_REQUEST_EVENTS.FORM_BACK_CLICKED);
    navigate(ACCESS_REQUESTS_ROUTE);
  };

  const getResourceOverview = (): string[] => {
    if (!formState.resource) return ["No resource selected"];
    const typeLabel =
      formState.resourceType.charAt(0).toUpperCase() +
      formState.resourceType.slice(1);
    return [`${typeLabel}: ${formState.resource.name}`];
  };

  const getActionOverview = (): string[] => {
    if (!formState.action) return ["No action selected"];
    return [
      formState.action
        .split("-")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" "),
    ];
  };

  const getTargetOverview = (): string[] => {
    if (!formState.target) return ["No target selected"];
    return [
      `${formState.targetType.charAt(0).toUpperCase() + formState.targetType.slice(1)}: ${formState.target.name}`,
    ];
  };

  const getOptionsOverview = (): string[] => {
    const items: string[] = [];
    if (formState.expirationDays === null) {
      items.push("Permanent access");
    } else {
      items.push(`Expires in ${formState.expirationDays} days`);
    }
    if (formState.shareable) items.push("Shareable");
    return items;
  };

  const getReasonOverview = (): string[] => {
    if (!formState.requestReason.trim()) return ["No reason provided"];
    const preview =
      formState.requestReason.length > 50
        ? formState.requestReason.substring(0, 50) + "..."
        : formState.requestReason;
    return [preview];
  };

  // Wait for the prefill metadata before rendering so we don't flash an empty
  // form and then jump the user to the final step.
  if (hasPrefill && prefillFetching) {
    return <Loading />;
  }

  return (
    <StyledForm noValidate onSubmit={handleSubmit}>
      <SidePadding>
        <StyledContainer>
          <StyledHeader>
            <Typography variant="h2">New Access Request</Typography>
            <BackButton
              type="button"
              onClick={handleBack}
              aria-label="Back to access requests"
            >
              <ChevronLeft />
            </BackButton>
          </StyledHeader>

          <FormStepper
            nonLinear
            activeStep={step}
            onStepSelect={handleStepSelect}
            activeIndex={step}
            contentPaddingBottom={2}
          >
            {[
              {
                label: "Select Target",
                tooltip:
                  "Request access for yourself, or for one of your roles or teams",
                overviewItems: getTargetOverview(),
                children: (
                  <RecipientSelectionField
                    targetType={formState.targetType}
                    value={formState.target}
                    onTargetTypeChange={(targetType) =>
                      updateFormState({ targetType, target: null })
                    }
                    onChange={handleTargetChange}
                  />
                ),
              },
              {
                label: "Select Resource",
                tooltip:
                  "Search for the resource you want to request access to",
                overviewItems: getResourceOverview(),
                children: (
                  <ResourcePicker
                    resourceType={formState.resourceType}
                    value={formState.resource}
                    onResourceTypeChange={(resourceType) =>
                      updateFormState({
                        resourceType,
                        resource: null,
                        action: null,
                      })
                    }
                    onChange={(resource) => {
                      const defaultScope = getDefaultScope(
                        resource,
                        resolveAccount,
                      );
                      updateFormState({
                        resource,
                        action: null,
                        scope: defaultScope,
                      });
                    }}
                    helperText={
                      formState.resource &&
                      (formState.scope ? (
                        <Typography variant="caption" color="text.secondary">
                          Detected Scope: {formState.scope.name} (
                          {formState.scope.type})
                        </Typography>
                      ) : formState.shareable ? (
                        <Typography variant="caption" color="error">
                          Scope could not be auto-detected for this resource,
                          and is required for shareable access. Contact an
                          admin.
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Scope could not be auto-detected for this resource.
                          This request will proceed without one.
                        </Typography>
                      ))
                    }
                  />
                ),
              },
              {
                label: "Select Action",
                tooltip:
                  "Choose what type of access you need for this resource",
                overviewItems: getActionOverview(),
                children: (
                  <ActionSelectionField
                    resource={formState.resource}
                    value={formState.action}
                    onChange={handleActionChange}
                  />
                ),
              },
              {
                label: "Additional Options",
                tooltip: "Configure additional settings",
                overviewItems: getOptionsOverview(),
                children: (
                  <AdditionalOptionsField
                    shareable={formState.shareable}
                    expirationDays={formState.expirationDays}
                    onShareableChange={(shareable) =>
                      updateFormState({ shareable })
                    }
                    onExpirationDaysChange={(expirationDays) =>
                      updateFormState({ expirationDays })
                    }
                  />
                ),
              },
              {
                label: "Request Reason",
                tooltip: "Explain why you need this access",
                overviewItems: getReasonOverview(),
                children: (
                  <RequestReasonField
                    value={formState.requestReason}
                    onChange={(requestReason) =>
                      updateFormState({ requestReason })
                    }
                    showValidation={showValidation}
                  />
                ),
              },
            ]}
          </FormStepper>
        </StyledContainer>
      </SidePadding>
      <StickyFooter elevation={3}>
        <FooterContent>
          <Button type="button" color="primary" onClick={handleCancel}>
            Cancel
          </Button>
          <SubmitArea>
            {submitError && (
              <SubmitError variant="body2" color="error" role="alert">
                <ErrorOutline fontSize="small" />
                {submitError}
              </SubmitError>
            )}
            <SubmitButton
              type="submit"
              color="primary"
              variant="contained"
              disabled={isSubmitting || !isFormValid(formState)}
            >
              {isSubmitting ? "Submitting..." : "Submit Request"}
            </SubmitButton>
          </SubmitArea>
        </FooterContent>
      </StickyFooter>
    </StyledForm>
  );
}

export default NewAccessRequestForm;
